import React, {
  useState,
  useEffect,
  useRef,
  lazy,
  Suspense,
  FormEvent,
} from "react";
import { cls } from "cls";
import { Input, InputGroup, Button } from "@clarkinc/zest-design-system/ui";
import encodeAndSanitizeSearchQuery from "~/common/helpers/encodeAndSanitizeSearchQuery";
import SearchIcon from "./Icons/SearchIcon";
import BarcodeScannerFallback from "./ProductBarcodeScanner/fallback";
import ImageSearchImageSelect from "./ImageSearchImageSelect";

const ImageSearchMenuButton = lazy(() => import("./ImageSearchMenuButton"));
const ImageSearchMenu = lazy(() => import("./ImageSearchMenu"));
const ProductBarcodeScanner = lazy(() => import("./ProductBarcodeScanner"));

interface Props {
  enableStorefrontAutosuggest: boolean;
}

const Search = ({ enableStorefrontAutosuggest }: Props) => {
  const [touchevents, setTouchevents] = useState(false);
  const [searchValue, setSearchValue] = useState(() => {
    if (!("document" in window)) return "";
    const input = window.document.querySelector<HTMLInputElement>("#searchval");
    return input?.value || "";
  });
  const [imageSelectOpen, setImageSelectOpen] = useState(false);
  const [barcodeScannerOpen, setBarcodeScannerOpen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const inputElementRef = useRef<HTMLInputElement>(null);

  let cancelTouchEvents = false;

  const searchInput = useRef<HTMLInputElement>(null);

  const form = useRef<HTMLFormElement>(null);

  const submitButton = useRef<HTMLButtonElement>(null);

  const initTouchevents = async () => {
    if (cancelTouchEvents) return;

    const hasTouchEvents = Boolean(
      "ontouchstart" in window || navigator.maxTouchPoints,
    );
    setTouchevents(hasTouchEvents);
  };

  const initSearchAutoComplete = async () => {
    const { default: SearchAutoComplete } = await import(
      "~/common/SearchAutocomplete"
    );
    const { current } = searchInput;
    if (!current || !submitButton.current) return;

    new SearchAutoComplete(
      current,
      submitButton.current,
      enableStorefrontAutosuggest,
    );

    const awesompleteContainer = document.querySelectorAll(
      "[id*='awesomplete_list']",
    );

    awesompleteContainer[0]?.setAttribute(
      "data-testid",
      "autocomplete-results",
    );
    for (const elem of awesompleteContainer) {
      elem.className =
        "z-[60] rounded mt-3 xs:mt-2.5 absolute bg-white list-none p-0 m-0 min-w-full overflow-hidden shadow-[0_5px_10px_rgba(0,0,0,0.2)]";
      const nextSibling = elem.nextElementSibling;
      nextSibling?.classList.add("sr-only");
      if (nextSibling?.getAttribute("aria-live") === "assertive") {
        nextSibling.setAttribute("aria-live", "polite");
      }
    }

    if (form.current) {
      form.current.addEventListener("submit", () => {
        import("storage-factory").then(({ storageFactory }) => {
          const storage = storageFactory(() => window.localStorage);

          storage.setItem("HeaderBannerSearchValue", current.value);
        });
      });
    }

    if (current.value) return;

    if (window.location.pathname.includes("/search/")) {
      const pageName = window.location.pathname.split("/").pop() as string;
      const searchTerm = decodeURIComponent(
        pageName
          .replace(/\.\w+$/, "")
          .replace(/-+/g, (match) => (match.length > 1 ? "-" : " ")),
      );
      const { storageFactory } = await import("storage-factory");

      const storage = storageFactory(() => window.localStorage);
      const headerSearchValue = storage.getItem("HeaderBannerSearchValue");

      if (searchTerm !== headerSearchValue) {
        current.value = searchTerm;
        setSearchValue(searchTerm);
      } else {
        current.value = headerSearchValue;
        setSearchValue(headerSearchValue);
      }
    }
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);

    const term = data.get("searchval")?.toString() || "";
    if (term) {
      const encoded = encodeAndSanitizeSearchQuery(term);
      window.location.href = `/search/${encoded}.html`;
      return;
    }
    window.location.href = "/search.html";
  };

  useEffect(() => {
    initTouchevents();

    initSearchAutoComplete();
    return function cleanup() {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      cancelTouchEvents = true;
    };
  }, []);

  function handleCancel() {
    if (form.current && searchInput.current) {
      form.current.reset();
      setSearchValue("");
      searchInput.current.focus();
    }
  }

  const placeholderText = "What are you looking for?";

  return (
    <div
      className="flex flex-initial relative w-full mx-auto mb-0 h-10"
      data-testid="search-input"
    >
      <div className="box-border pr-0 relative w-full">
        <form
          ref={form}
          method="get"
          action="/search.html"
          className="flex w-full m-0"
          id="searchForm"
          role="search"
          onSubmit={onSubmit}
        >
          <div className="flex flex-nowrap h-10 w-full">
            <InputGroup groupLabel="Search" theme="wss" inputSize="large">
              <Input
                hideLabel
                type="text"
                ref={searchInput}
                name="searchval"
                id="searchval"
                data-testid="searchval"
                autoComplete="off"
                onValueChange={setSearchValue}
                theme="wss"
                label=""
                aria-label="Search: type a search term, and navigate through results with up and down arrows"
                placeholder={placeholderText}
                className={cls(
                  "lt:rounded-r-none",
                  "lt:focus:rounded-r",
                  "text-base",
                  "focus:placeholder-transparent",
                  "shadow-none",
                  touchevents ? "pl-[11px]" : "pl-3",
                )}
                suffix={
                  <>
                    <Button
                      theme="wss"
                      key={searchValue}
                      type="button"
                      color="gray"
                      size="medium"
                      onClick={handleCancel}
                      aria-label="Clear Search"
                      className={cls(!searchValue && "hidden", "shadow-none")}
                      iconProps={{
                        name: "ri-close-line",
                        className: "fill-current",
                      }}
                    />
                    {!searchValue && touchevents ? (
                      <Suspense>
                        <ImageSearchMenuButton
                          showMenu={showMenu}
                          setShowMenu={setShowMenu}
                        />
                      </Suspense>
                    ) : null}
                  </>
                }
                wrapperProps={{ className: "w-full relative" }}
                defaultValue={searchValue}
              />
              {!searchValue && touchevents ? (
                <ImageSearchMenu
                  isOpened={showMenu}
                  closeMenu={() => setShowMenu(false)}
                  setBarcodeScannerOpen={setBarcodeScannerOpen}
                  inputElementRef={inputElementRef}
                  setImageSelectOpen={setImageSelectOpen}
                />
              ) : null}
              <ImageSearchImageSelect
                ref={inputElementRef}
                isOpen={imageSelectOpen}
                onClose={() => setImageSelectOpen(false)}
              />
              {barcodeScannerOpen ? (
                <Suspense fallback={<BarcodeScannerFallback />}>
                  <ProductBarcodeScanner
                    isOpen={barcodeScannerOpen}
                    close={() => setBarcodeScannerOpen(false)}
                  />
                </Suspense>
              ) : null}
              <Button
                ref={submitButton}
                theme="wss"
                color="blue"
                value="Search"
                type="submit"
                className="hidden lt:flex rounded-none rounded-r py-2.5 px-4-1/2 tracking-[.02em]"
              >
                <span className="sr-only">Search WebstaurantStore</span>
                <SearchIcon />
              </Button>
            </InputGroup>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Search;
